'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { ExclamationMarkTriangleXs } from '@lichtblick/photon/icons/symbols/exclamation-mark-triangle-xs';
import { FieldFooter } from '@lichtblick/photon/molecules/legacy-field-footer/field-footer';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { startTransition, useActionState, useCallback, useContext, useEffect, useRef } from 'react';
import { submitForm } from './actions';
import { Checkbox } from './Checkbox';
import { Confirmation } from './Confirmation';
import { ErrorContext } from './ErrorBoundary';
import { COMMODITIES } from './Form.content';
import { debounce, getFormError, getFormProduct } from './Form.helpers';
import styles from './Form.module.scss';
import { OptionalSubsection } from './OptionalSubsection';
import { Textarea } from './Textarea';
import { Textfield } from './Textfield';
export const Form = ({ commodity })=>{
    const formRef = useRef(null);
    const { trackCustomEvent } = useTracker();
    const { outerError } = useContext(ErrorContext); // Note: Form data is lost on outer error, but that's fine for now
    const [state, dispatch, pending] = useActionState(submitForm, {});
    const { error, input, trackingId } = state;
    const errorMessage = outerError ? 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.' : error;
    const { fields, formName, headline, option } = COMMODITIES[commodity];
    const trackFocus = useCallback(()=>trackCustomEvent({
            eventName: 'form_open',
            customProps: {
                form_name: formName
            }
        }), [
        formName,
        trackCustomEvent
    ]);
    const handleFieldError = useCallback(()=>{
        const invalidFields = formRef.current?.querySelectorAll(':is(input, select, textarea):invalid');
        invalidFields?.[0]?.focus();
        trackCustomEvent({
            eventName: 'form_failed',
            customProps: {
                form_name: formName,
                error_message: getFormError(invalidFields)
            }
        });
    }, [
        formName,
        trackCustomEvent
    ]);
    const handleFailedSubmit = useCallback(()=>debounce(handleFieldError), [
        handleFieldError
    ]);
    useEffect(()=>{
        if (trackingId) {
            trackCustomEvent({
                eventName: `form_submit_B2B_${getFormProduct(input)}`,
                customProps: {
                    unique_id: trackingId
                }
            });
        } else if (errorMessage) {
            trackCustomEvent({
                eventName: 'form_failed',
                customProps: {
                    form_name: formName,
                    error_message: errorMessage
                }
            });
        }
    }, [
        errorMessage,
        formName,
        input,
        trackingId,
        trackCustomEvent
    ]);
    if (trackingId) {
        return /*#__PURE__*/ _jsx(Confirmation, {
            email: input.emailAddress,
            onBackButtonClick: ()=>startTransition(dispatch)
        });
    }
    const getProps = (name, optional = false)=>({
            defaultValue: input?.[name]?.toString(),
            name,
            required: !optional,
            subLabel: optional ? 'optional' : undefined
        });
    return /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsx(Headline, {
                className: styles.headline,
                renderAs: "h2",
                size: "m",
                children: headline
            }),
            /*#__PURE__*/ _jsx(Headline, {
                className: styles['sub-headline'],
                renderAs: "h3",
                size: "s",
                children: "Einfach Formular ausfüllen und ein*e LichtBlick-Berater*in meldet sich."
            }),
            /*#__PURE__*/ _jsxs("form", {
                action: dispatch,
                onFocus: trackFocus,
                onInvalid: handleFailedSubmit,
                ref: formRef,
                children: [
                    /*#__PURE__*/ _jsxs("fieldset", {
                        className: styles.section,
                        children: [
                            /*#__PURE__*/ _jsx(Headline, {
                                renderAs: "h4",
                                size: "s",
                                children: "Firmenadresse"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('companyName'),
                                className: styles.field,
                                label: "Unternehmen"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('street'),
                                className: styles.field,
                                label: "Straße"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('houseNumber'),
                                className: styles.field,
                                label: "Hausnummer"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('postalCode'),
                                className: styles.field,
                                label: "Postleitzahl",
                                messages: {
                                    pattern: 'Ungültige Postleitzahl'
                                },
                                pattern: "^\\d{5}$"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('city'),
                                className: styles.field,
                                label: "Ort"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        className: styles.section,
                        children: [
                            /*#__PURE__*/ _jsx(Headline, {
                                renderAs: "h4",
                                size: "s",
                                children: "Ansprechperson"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('firstname'),
                                className: styles.field,
                                label: "Vorname"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('lastname'),
                                className: styles.field,
                                label: "Nachname"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('emailAddress'),
                                className: styles.field,
                                label: "E-Mail-Adresse",
                                messages: {
                                    invalid: 'Ungültige E-Mail-Adresse'
                                },
                                type: "email"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps('telephoneNumber'),
                                className: styles.field,
                                label: "Telefonnummer",
                                messages: {
                                    pattern: 'Ungültige Telefonnummer'
                                },
                                pattern: "^\\(?(\\+\\d|0)([ \\(\\)\\/\\-]*\\d){5,}$",
                                type: "tel"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        className: styles.section,
                        children: [
                            /*#__PURE__*/ _jsx(Headline, {
                                renderAs: "h4",
                                size: "s",
                                children: "Ihr Verbrauch"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps(fields.consumption),
                                className: styles.field,
                                label: "Jahresplanverbrauch",
                                min: "0",
                                suffixText: "kWh",
                                type: "number"
                            }),
                            /*#__PURE__*/ _jsx(Textfield, {
                                ...getProps(fields.meters, true),
                                className: styles.field,
                                label: "Anzahl Zähler",
                                min: "0",
                                type: "number"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        className: styles.section,
                        children: [
                            /*#__PURE__*/ _jsx(Headline, {
                                renderAs: "h4",
                                size: "s",
                                children: option.headline
                            }),
                            /*#__PURE__*/ _jsx(OptionalSubsection, {
                                icon: option.icon,
                                isOpen: Boolean(input?.[option.fields.consumption]),
                                label: option.label,
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: styles.subsection,
                                    children: [
                                        /*#__PURE__*/ _jsx(Textfield, {
                                            ...getProps(option.fields.consumption),
                                            className: styles.field,
                                            label: "Jahresplanverbrauch",
                                            min: "0",
                                            suffixText: "kWh",
                                            type: "number"
                                        }),
                                        /*#__PURE__*/ _jsx(Textfield, {
                                            ...getProps(option.fields.meters, true),
                                            className: styles.field,
                                            label: "Anzahl Zähler",
                                            min: "0",
                                            type: "number"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Textarea, {
                                ...getProps('customerMessage', true),
                                className: classNames(styles.field, styles.spaced),
                                label: "Nachricht",
                                rows: 4
                            }),
                            /*#__PURE__*/ _jsx(Checkbox, {
                                labelSlot: /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        "Ja, ich willige ein, dass mich LichtBlick sowie mit LichtBlick verbundene Unter­nehmen über Strom-, FahrStrom und Gas-Produkte sowie über energienahe Produkte und Dienstleistungen (z.B. Smartmeter, Wallboxen, alternative Energie­anlagen oder Energieberatung), Bonusaktionen (z.B. „Kunden werben Kunden“), sowie zu Marktforschungszwecken und zur Qualitätssicherung telefonisch oder per E-Mail kontaktieren und informieren. Ich habe die",
                                        ' ',
                                        /*#__PURE__*/ _jsx(TextCTA, {
                                            as: "a",
                                            href: "/datenschutz/",
                                            inlineLink: true,
                                            target: "_blank",
                                            children: "Datenschutzhinweise"
                                        }),
                                        ' ',
                                        "zur Kenntnis genommen und bin darüber informiert, dass die vorstehende, freiwillige Einwilligung jederzeit ohne Angabe von Gründen mit Wirkung für die Zukunft (per Post an LichtBlick SE, Klostertor 1, 20097 Hamburg, per E-Mail an",
                                        ' ',
                                        /*#__PURE__*/ _jsx(TextCTA, {
                                            as: "a",
                                            href: "mailto:datenschutz@lichtblick.de",
                                            inlineLink: true,
                                            children: "datenschutz@lichtblick.de"
                                        }),
                                        ' ',
                                        "oder per Telefon unter",
                                        ' ',
                                        /*#__PURE__*/ _jsx(TextCTA, {
                                            as: "a",
                                            href: "tel:+494080803034",
                                            inlineLink: true,
                                            children: "040 80803034"
                                        }),
                                        ") widerrufen werden kann."
                                    ]
                                }),
                                name: "marketingOptIn",
                                required: true
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(BoxCTA, {
                        as: "button",
                        className: styles.cta,
                        loading: pending,
                        type: "submit",
                        variant: "primary",
                        children: "Jetzt abschicken"
                    }),
                    errorMessage && !pending && /*#__PURE__*/ _jsx(FieldFooter, {
                        className: styles['form-error'],
                        icon: ExclamationMarkTriangleXs,
                        label: errorMessage,
                        state: "error"
                    })
                ]
            })
        ]
    });
};

//# sourceMappingURL=Form.js.map